import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface NavigationHistoryItem {
  path: string;
  title: string;
  timestamp: Date;
  entityType?: string;
  entityId?: string | number;
}

interface UseNavigationHistoryResult {
  history: NavigationHistoryItem[];
  canGoBack: boolean;
  canGoForward: boolean;
  goBack: () => void;
  goForward: () => void;
  clearHistory: () => void;
  goToHistoryIndex: (index: number) => void;
}

const MAX_HISTORY_ITEMS = 50;

class NavigationHistory {
  private history: NavigationHistoryItem[] = [];
  private currentIndex: number = -1;
  
  constructor() {
    // Load history from localStorage
    const saved = localStorage.getItem('navigation_history');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        this.history = parsed.history || [];
        this.currentIndex = parsed.currentIndex || -1;
      } catch (e) {
        console.error('Failed to parse navigation history:', e);
      }
    }
  }
  
  push(item: NavigationHistoryItem) {
    // Remove any forward history
    this.history = this.history.slice(0, this.currentIndex + 1);
    
    // Add new item
    this.history.push(item);
    
    // Limit history size
    if (this.history.length > MAX_HISTORY_ITEMS) {
      this.history = this.history.slice(-MAX_HISTORY_ITEMS);
    }
    
    this.currentIndex = this.history.length - 1;
    this.save();
  }
  
  goBack(): NavigationHistoryItem | null {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.save();
      return this.history[this.currentIndex];
    }
    return null;
  }
  
  goForward(): NavigationHistoryItem | null {
    if (this.currentIndex < this.history.length - 1) {
      this.currentIndex++;
      this.save();
      return this.history[this.currentIndex];
    }
    return null;
  }
  
  goToIndex(index: number): NavigationHistoryItem | null {
    if (index >= 0 && index < this.history.length) {
      this.currentIndex = index;
      this.save();
      return this.history[this.currentIndex];
    }
    return null;
  }
  
  clear() {
    this.history = [];
    this.currentIndex = -1;
    this.save();
  }
  
  getHistory(): NavigationHistoryItem[] {
    return [...this.history];
  }
  
  canGoBack(): boolean {
    return this.currentIndex > 0;
  }
  
  canGoForward(): boolean {
    return this.currentIndex < this.history.length - 1;
  }
  
  private save() {
    localStorage.setItem('navigation_history', JSON.stringify({
      history: this.history,
      currentIndex: this.currentIndex
    }));
  }
}

// Singleton instance
const navigationHistory = new NavigationHistory();

export function useNavigationHistory(): UseNavigationHistoryResult {
  const location = useLocation();
  const navigate = useNavigate();
  const isNavigatingRef = useRef(false);
  
  useEffect(() => {
    if (!isNavigatingRef.current) {
      // Parse path to extract entity information
      const pathParts = location.pathname.split('/').filter(Boolean);
      const entityType = pathParts[0];
      const entityId = pathParts[1];
      
      // Create title based on path
      let title = entityType ? entityType.charAt(0).toUpperCase() + entityType.slice(1) : 'Home';
      if (entityId) {
        title += ` #${entityId}`;
      }
      
      navigationHistory.push({
        path: location.pathname,
        title,
        timestamp: new Date(),
        entityType,
        entityId
      });
    }
    isNavigatingRef.current = false;
  }, [location]);
  
  const goBack = () => {
    isNavigatingRef.current = true;
    const item = navigationHistory.goBack();
    if (item) {
      navigate(item.path);
    }
  };
  
  const goForward = () => {
    isNavigatingRef.current = true;
    const item = navigationHistory.goForward();
    if (item) {
      navigate(item.path);
    }
  };
  
  const goToHistoryIndex = (index: number) => {
    isNavigatingRef.current = true;
    const item = navigationHistory.goToIndex(index);
    if (item) {
      navigate(item.path);
    }
  };
  
  const clearHistory = () => {
    navigationHistory.clear();
  };
  
  return {
    history: navigationHistory.getHistory(),
    canGoBack: navigationHistory.canGoBack(),
    canGoForward: navigationHistory.canGoForward(),
    goBack,
    goForward,
    clearHistory,
    goToHistoryIndex
  };
}

export default useNavigationHistory;